import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    getProfessorAdminOverview,
    createProfessorTeam,
    deleteProfessorTeam,
    createProfessorPlayer,
    updateProfessorPlayer,
    deleteProfessorPlayer,
    moveProfessorPlayerTeam,
    getLeagueRankingForProfessor,
} from '../../../Api/authApi.js.js';
import { formatPrice } from '../../../functions/price-formater.js';
import { Skeleton } from '../../components/Skeleton';
import './administratif.css';

// Durée de l'animation de fermeture des modals — doit correspondre à celle définie dans administratif.css.
const MODAL_CLOSE_ANIM_MS = 180;

const fmtPct = (n) => {
    const v = parseFloat(n) || 0;
    return (v >= 0 ? '+' : '') + v.toFixed(2) + ' %';
};

// Export CSV côté client (séparateur ';' + BOM pour Excel FR), pas de nouvelle dépendance XLSX.
const csvEscape = (value) => {
    const s = String(value ?? '');
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, headers, rows) => {
    const lines = [headers, ...rows].map((row) => row.map(csvEscape).join(';'));
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// Select custom (le <select> natif ouvre un menu au style non maîtrisable — texte clair sur fond
// clair du navigateur, seule la ligne survolée redevenait lisible). Même pattern que UserMenuButton :
// bouton + panneau positionné en absolu, fermé au clic extérieur.
function TeamSelect({ teams, value, onChange }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const selected = teams.find((t) => t.id === value);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} style={{ position: 'relative', width: 150, flex: '0 0 auto' }}>
            <button type="button" className="prof-administratif-select-trigger" onClick={() => setOpen((o) => !o)}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected?.team_name || '—'}</span>
                <svg width={14} height={14} viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                    <path d="M6 9l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && (
                <div className="prof-administratif-select-menu">
                    {teams.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            className={`prof-administratif-select-option${t.id === value ? ' selected' : ''}`}
                            onClick={() => { setOpen(false); if (t.id !== value) onChange(t.id); }}
                        >
                            {t.team_name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function IconEdit({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0 }}>
            <path d="M14 5l3 3-9 9H5v-3l9-9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
            <path d="M13 6l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}

function IconTrash({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0 }}>
            <path d="M5 7h12M9 7V5a1 1 0 011-1h2a1 1 0 011 1v2M7 7l1 12a1 1 0 001 1h6a1 1 0 001-1l1-12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 10.5v6M12 10.5v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}

// Icône œil / œil barré + toggle affichage — même SVG que loginpage/signuppage.
function IconEye({ open, size = 16 }) {
    return open ? (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
    ) : (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    );
}

function PasswordInput({ value, onChange }) {
    const [visible, setVisible] = useState(false);
    return (
        <div style={{ position: 'relative', marginTop: 6 }}>
            <input
                className="prof-administratif-input"
                style={{ paddingRight: 38 }}
                type={visible ? 'text' : 'password'}
                value={value}
                onChange={onChange}
            />
            <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-mute)', padding: 0, display: 'flex',
                }}
            >
                <IconEye open={visible} />
            </button>
        </div>
    );
}

function IconDownload({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0 }}>
            <path d="M11 3v10m0 0l-3.5-3.5M11 13l3.5-3.5M4 17.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function Modal({ title, onClose, hideClose = false, closing = false, children }) {
    return (
        <div className={`prof-administratif-overlay${closing ? ' closing' : ''}`} onClick={onClose}>
            <div className={`relief prof-administratif-modal${closing ? ' closing' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
                    {!hideClose && (
                        <button type="button" className="prof-administratif-btn small" onClick={onClose}>Fermer</button>
                    )}
                </div>
                {children}
            </div>
        </div>
    );
}

function CreateTeamForm({ onSubmit, onClose }) {
    const [teamName, setTeamName] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!teamName.trim()) return;
        setBusy(true);
        try {
            await onSubmit(teamName.trim());
            onClose();
        } catch {
            // Toast déjà affiché par le handler parent ; on laisse juste la modal ouverte.
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit}>
            <label style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
                Nom de l'équipe
                <input className="prof-administratif-input" style={{ marginTop: 6 }} value={teamName}
                    onChange={(e) => setTeamName(e.target.value)} placeholder="Ex. Les Loups" autoFocus />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button type="button" className="prof-administratif-btn" onClick={onClose}>Annuler</button>
                <button type="submit" className="prof-administratif-btn gold" disabled={busy || !teamName.trim()}>
                    {busy ? 'Création…' : 'Valider'}
                </button>
            </div>
        </form>
    );
}

function PlayerForm({ mode = 'create', initialUsername = '', email = '', onSubmit, onClose }) {
    const isEdit = mode === 'edit';
    const [form, setForm] = useState({ username: initialUsername, email, password: '', password_confirmation: '' });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await onSubmit(form);
            onClose();
        } catch {
            // Toast déjà affiché par le handler parent ; on laisse juste la modal ouverte.
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit}>
            <div className="my-5">
                <label className="mb-4" style={{ display: 'block', fontSize: 12.5, color: 'var(--text-dim)' }}>
                    Nom d'utilisateur
                    <input className="prof-administratif-input" style={{ marginTop: 6 }} value={form.username} onChange={set('username')} autoFocus />
                </label>
                <label className="mb-4" style={{ display: 'block', fontSize: 12.5, color: 'var(--text-dim)' }}>
                    Email
                    <input className="prof-administratif-input" style={{ marginTop: 6 }} type="email" value={form.email}
                        onChange={set('email')} disabled={isEdit} />
                </label>
                <label className="mb-4" style={{ display: 'block', fontSize: 12.5, color: 'var(--text-dim)' }}>
                    Mot de passe{isEdit ? ' (laisser vide pour ne pas modifier)' : ''}
                    <PasswordInput value={form.password} onChange={set('password')} />
                </label>
                <label className="mb-4" style={{ display: 'block', fontSize: 12.5, color: 'var(--text-dim)' }}>
                    Confirmation du mot de passe
                    <PasswordInput value={form.password_confirmation} onChange={set('password_confirmation')} />
                </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="prof-administratif-btn" onClick={onClose}>Annuler</button>
                <button type="submit" className="prof-administratif-btn gold" disabled={busy || !form.username.trim()}>
                    {busy ? 'Enregistrement…' : 'Valider'}
                </button>
            </div>
        </form>
    );
}

function AdministratifSkeleton() {
    return (
        <div className="prof-administratif-layout" style={{ maxWidth: '1160px' }}>
            <div className="relief" style={{ padding: 8, borderRadius: 16 }}>
                <div className="prof-administratif-team-list">
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} style={{
                            padding: '11px 12px', borderRadius: 10, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                        }}>
                            <Skeleton width={90} height={14} radius={4} />
                            <Skeleton width={16} height={11.5} radius={4} />
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <div className="relief" style={{ padding: 18, borderRadius: 16, marginBottom: 14 }}>
                    <div style={{ marginBottom: 3 }}><Skeleton width={140} height={19} radius={4} /></div>
                    <div style={{ marginBottom: 10 }}><Skeleton width={180} height={12.5} radius={4} /></div>
                    <div style={{ display: 'flex', gap: 18 }}>
                        {[0, 1].map(i => (
                            <div key={i}>
                                <div style={{ marginBottom: 4 }}><Skeleton width={60} height={16} radius={4} /></div>
                                <Skeleton width={90} height={11} radius={4} />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relief" style={{ padding: 4, borderRadius: 16, marginBottom: 14 }}>
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 14px',
                            borderBottom: i < 3 ? '1px solid var(--border)' : 'none',
                        }}>
                            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                <div style={{ marginBottom: 6 }}><Skeleton width={100} height={14} radius={4} /></div>
                                <Skeleton width={140} height={11.5} radius={4} />
                            </div>
                            <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                                <div style={{ marginBottom: 4 }}><Skeleton width={70} height={13.5} radius={4} /></div>
                                <Skeleton width={45} height={11.5} radius={4} style={{ marginLeft: 'auto' }} />
                            </div>
                            <Skeleton width={95} height={36} radius={8} />
                            <Skeleton width={150} height={36} radius={8} />
                            <Skeleton width={36} height={36} radius={8} />
                            <Skeleton width={36} height={36} radius={8} />
                        </div>
                    ))}
                </div>

                <Skeleton width={150} height={38} radius={10} />
            </div>
        </div>
    );
}

const Administratif = () => {
    const professeurId = useSelector((state) => state.auth.user?.id);

    const [overview, setOverview] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | empty | error
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [ranking, setRanking] = useState(null);
    const [rankingStatus, setRankingStatus] = useState('loading'); // loading | ready | unavailable
    const [modal, setModal] = useState(null); // { type, teamId?, playerId? }
    const [modalClosing, setModalClosing] = useState(false);

    const load = useCallback(() => {
        if (!professeurId) return;
        setStatus('loading');
        getProfessorAdminOverview(professeurId)
            .then((data) => {
                setOverview(data);
                setSelectedTeamId((prev) => (data.teams.some((t) => t.id === prev) ? prev : data.teams[0]?.id || null));
                setStatus(data.teams.length ? 'ready' : 'empty');
            })
            .catch(() => setStatus('error'));
    }, [professeurId]);

    const loadRanking = useCallback(() => {
        if (!professeurId) return;
        setRankingStatus('loading');
        getLeagueRankingForProfessor(professeurId)
            .then((data) => { setRanking(data); setRankingStatus('ready'); })
            .catch(() => setRankingStatus('unavailable'));
    }, [professeurId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadRanking(); }, [loadRanking]);

    const selectedTeam = overview?.teams.find((t) => t.id === selectedTeamId) || overview?.teams[0];
    const rankingTeam = ranking?.teams?.find((t) => t.team_id === selectedTeam?.id);
    const rankingMemberFor = (userId) => rankingTeam?.members?.find((m) => m.user_id === userId);

    const closeModal = () => {
        setModalClosing(true);
        setTimeout(() => {
            setModal(null);
            setModalClosing(false);
        }, MODAL_CLOSE_ANIM_MS);
    };

    const handleCreateTeam = async (teamName) => {
        try {
            await createProfessorTeam(professeurId, teamName);
            toast.success('Équipe créée avec succès.');
            load();
        } catch (error) {
            toast.warning(error.message);
            throw error;
        }
    };

    const handleDeleteTeam = async (teamId) => {
        try {
            await deleteProfessorTeam(professeurId, teamId);
            toast.success('Équipe supprimée avec succès.');
            setSelectedTeamId(null);
            load();
        } catch (error) {
            toast.warning(error.message);
        } finally {
            closeModal();
        }
    };

    const handleAddPlayer = async (form) => {
        try {
            await createProfessorPlayer(professeurId, selectedTeam.id, form);
            toast.success('Élève ajouté avec succès.');
            load();
            loadRanking();
        } catch (error) {
            toast.warning(error.message);
            throw error;
        }
    };

    const handleDeletePlayer = async (userId) => {
        try {
            await deleteProfessorPlayer(professeurId, userId);
            toast.success('Élève supprimé avec succès.');
            load();
            loadRanking();
        } catch (error) {
            toast.warning(error.message);
        } finally {
            closeModal();
        }
    };

    const handleMovePlayer = async (userId, teamId) => {
        try {
            await moveProfessorPlayerTeam(professeurId, userId, teamId);
            toast.success('Élève déplacé avec succès.');
            load();
            loadRanking();
        } catch (error) {
            toast.warning(error.message);
        }
    };

    const handleUpdatePlayer = async (userId, form) => {
        try {
            await updateProfessorPlayer(professeurId, userId, form);
            toast.success('Élève modifié avec succès.');
            load();
            loadRanking();
        } catch (error) {
            toast.warning(error.message);
            throw error;
        }
    };

    const formatPerf = (perf) => ({
        capital: perf ? (parseFloat(perf.capital) || 0).toFixed(2) : '',
        pct: perf ? (parseFloat(perf.montant_individuel) || 0).toFixed(2) : '',
    });

    const exportTeamCsv = () => {
        if (!selectedTeam || !rankingTeam) return;
        const rows = selectedTeam.members.map((m) => {
            const perf = formatPerf(rankingMemberFor(m.id));
            return [m.username, m.email, perf.capital, perf.pct];
        });
        downloadCsv(`classement_${selectedTeam.team_name}.csv`, ['Élève', 'Email', 'Capital (€)', 'Performance (%)'], rows);
    };

    const exportPlayerCsv = (member) => {
        const perf = formatPerf(rankingMemberFor(member.id));
        downloadCsv(`classement_${member.username}.csv`,
            ['Élève', 'Email', 'Équipe', 'Capital (€)', 'Performance (%)'],
            [[member.username, member.email, selectedTeam.team_name, perf.capital, perf.pct]]);
    };

    return (
        <div className="prof-administratif-page">
            <div style={{ padding: '4px 4px 4px' }}>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' }}>Administratif</h1>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '0.08em', color: 'var(--text-mute)', textTransform: 'uppercase', marginTop: 6 }}>
                    Gestion de votre ligue{overview?.league_name ? ` · ${overview.league_name}` : ''}
                </div>
            </div>

            {status === 'loading' && <AdministratifSkeleton />}
            {status === 'error' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--down)', fontSize: 13 }}>
                    Impossible de charger les équipes de la ligue.
                </div>
            )}

            {(status === 'ready' || status === 'empty') && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, maxWidth: '1160px' }}>
                        <button type="button" className="prof-administratif-btn primary"
                            onClick={() => setModal({ type: 'createTeam' })}>+ Créer une équipe</button>
                    </div>

                    {status === 'empty' && (
                        <div className="relief" style={{ marginTop: 14, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                            Aucune équipe pour votre ligue. Créez-en une pour commencer à ajouter des élèves.
                        </div>
                    )}

                    {status === 'ready' && selectedTeam && (
                        <div className="prof-administratif-layout" style={{maxWidth: '1160px'}}>
                            <div className="relief" style={{ padding: 8, borderRadius: 16 }}>
                                <div className="prof-administratif-team-list">
                                    {overview.teams.map((t) => (
                                        <div key={t.id} onClick={() => setSelectedTeamId(t.id)} className={'pill-tap' + (t.id === selectedTeam.id ? '' : ' hover:!bg-black/[0.1]')}
                                            style={{
                                                padding: '11px 12px', borderRadius: 10, cursor: 'pointer', flexShrink: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                                                background: t.id === selectedTeam.id ? 'rgba(255,255,255,0.06)' : 'transparent',
                                            }}>
                                            <span style={{
                                                fontSize: 14, whiteSpace: 'nowrap',
                                                fontWeight: t.id === selectedTeam.id ? 600 : 500,
                                                color: t.id === selectedTeam.id ? 'var(--text)' : 'var(--text-dim)',
                                            }}>{t.team_name}</span>
                                            <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-mute)' }}>{t.members.length}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div className="relief" style={{ padding: 18, borderRadius: 16, marginBottom: 14 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
                                        <div>
                                            <div style={{ fontSize: 19, fontWeight: 600, marginBottom: 3 }}>{selectedTeam.team_name}</div>
                                            <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-mute)' }}>
                                                Code {selectedTeam.team_code} · {selectedTeam.members.length} élève{selectedTeam.members.length > 1 ? 's' : ''}
                                            </div>
                                            {rankingTeam && (
                                                <div style={{ display: 'flex', gap: 18, marginTop: 10 }}>
                                                    <div>
                                                        <div className="mono num" style={{ fontSize: 16, fontWeight: 700 }}>
                                                            {formatPrice(selectedTeam.members.reduce((s, m) => s + (parseFloat(rankingMemberFor(m.id)?.capital) || 0), 0))}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: 'var(--text-mute)' }}>Capital total</div>
                                                    </div>
                                                    <div>
                                                        <div className="mono num" style={{ fontSize: 16, fontWeight: 700, color: (parseFloat(rankingTeam.total_montant) || 0) >= 0 ? 'var(--up)' : 'var(--down)' }}>
                                                            {fmtPct(rankingTeam.total_montant)}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: 'var(--text-mute)' }}>Performance moyenne</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                            <button type="button" className="prof-administratif-icon-btn"
                                                disabled={rankingStatus !== 'ready'}
                                                title={rankingStatus !== 'ready' ? 'Classement indisponible (aucun marché actif pour la ligue).' : undefined}
                                                onClick={exportTeamCsv}><IconDownload />Classement de l'équipe</button>
                                            <button type="button" className="prof-administratif-btn danger"
                                                disabled={selectedTeam.members.length > 0}
                                                title={selectedTeam.members.length > 0 ? 'Déplacez ou supprimez d\'abord les élèves de cette équipe.' : undefined}
                                                onClick={() => setModal({ type: 'deleteTeam', teamId: selectedTeam.id })}><IconTrash />Supprimer l'équipe</button>
                                        </div>
                                    </div>
                                </div>

                                <div className="relief" style={{ padding: 4, borderRadius: 16, marginBottom: 14 }}>
                                    {selectedTeam.members.length === 0 && (
                                        <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                                            Aucun élève dans cette équipe.
                                        </div>
                                    )}
                                    {selectedTeam.members.map((m, i) => (
                                        <div key={m.id} className="prof-administratif-player-row"
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 14px',
                                                borderBottom: i < selectedTeam.members.length - 1 ? '1px solid var(--border)' : 'none',
                                            }}>
                                            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                                <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.username}</div>
                                                <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-mute)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>
                                            </div>

                                            {rankingMemberFor(m.id) && (
                                                <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                                                    <div className="mono num" style={{ fontSize: 13.5, fontWeight: 700 }}>{formatPrice(rankingMemberFor(m.id).capital)}</div>
                                                    <div className="mono num" style={{ fontSize: 11.5, color: (parseFloat(rankingMemberFor(m.id).montant_individuel) || 0) >= 0 ? 'var(--up)' : 'var(--down)' }}>
                                                        {fmtPct(rankingMemberFor(m.id).montant_individuel)}
                                                    </div>
                                                </div>
                                            )}

                                            <button type="button" className="prof-administratif-icon-btn"
                                                disabled={rankingStatus !== 'ready'}
                                                title={rankingStatus !== 'ready' ? 'Classement indisponible (aucun marché actif pour la ligue).' : undefined}
                                                onClick={() => exportPlayerCsv(m)}><IconDownload />Classement</button>

                                            <TeamSelect
                                                teams={overview.teams}
                                                value={selectedTeam.id}
                                                onChange={(teamId) => handleMovePlayer(m.id, teamId)}
                                            />

                                            <button type="button" className="prof-administratif-icon-btn" title="Modifier"
                                                onClick={() => setModal({ type: 'editPlayer', playerId: m.id })}><IconEdit /></button>
                                            <button type="button" className="prof-administratif-icon-btn danger" title="Supprimer"
                                                onClick={() => setModal({ type: 'deletePlayer', playerId: m.id })}><IconTrash /></button>
                                        </div>
                                    ))}
                                </div>

                                <button type="button" className="prof-administratif-btn primary"
                                    onClick={() => setModal({ type: 'addPlayer' })}>+ Ajouter un élève</button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {modal?.type === 'createTeam' && (
                <Modal title="Créer une équipe" onClose={closeModal} closing={modalClosing} hideClose>
                    <CreateTeamForm onClose={closeModal} onSubmit={handleCreateTeam} />
                </Modal>
            )}

            {modal?.type === 'addPlayer' && (
                <Modal title={`Ajouter un élève — ${selectedTeam?.team_name}`} onClose={closeModal} closing={modalClosing} hideClose>
                    <PlayerForm mode="create" onClose={closeModal} onSubmit={handleAddPlayer} />
                </Modal>
            )}

            {modal?.type === 'editPlayer' && (
                <Modal title={`Modifier — ${selectedTeam?.members.find((m) => m.id === modal.playerId)?.username}`} onClose={closeModal} closing={modalClosing} hideClose>
                    <PlayerForm
                        mode="edit"
                        initialUsername={selectedTeam?.members.find((m) => m.id === modal.playerId)?.username}
                        email={selectedTeam?.members.find((m) => m.id === modal.playerId)?.email}
                        onClose={closeModal}
                        onSubmit={(form) => handleUpdatePlayer(modal.playerId, form)}
                    />
                </Modal>
            )}

            {modal?.type === 'deleteTeam' && (
                <Modal title="Supprimer l'équipe ?" onClose={closeModal} closing={modalClosing}>
                    <p style={{ fontSize: 13.5, color: 'var(--text-dim)', margin: 0 }}>
                        Cette action est définitive. L'équipe « {selectedTeam?.team_name} » sera supprimée.
                    </p>
                    <button type="button" className="prof-administratif-btn danger" onClick={() => handleDeleteTeam(modal.teamId)}>Confirmer</button>
                </Modal>
            )}

            {modal?.type === 'deletePlayer' && (
                <Modal title={`Supprimer l'élève ${selectedTeam?.members.find((m) => m.id === modal.playerId)?.username} ?`} onClose={closeModal} closing={modalClosing} hideClose>
                    <p style={{ fontSize: 13.5, color: 'var(--text-dim)', margin: 0 }}>
                        Cette action est définitive.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                        <button type="button" className="prof-administratif-btn" onClick={closeModal}>Annuler</button>
                        <button type="button" className="prof-administratif-btn danger" onClick={() => handleDeletePlayer(modal.playerId)}>Confirmer</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Administratif;
